/**
 * @class DrawerModal
 * @classdesc A drawer modal component that can support multiple flyouts with content within them.
 *
 * @extends Core.Component
 */
export default class DrawerModal extends Core.Component {
  constructor(el, context) {
    super(el, context);

    this.template = el.querySelector('[data-drawer-flyout-template]');
    this.events.register(window, 'keyup', e => { if (e.key === 'Escape') this.closeAllFlyouts(); });
    this.events.register(document, 'click', e => {
      if (e.target.tagName !== 'SHOPIFY-INSTALLMENTS-MODAL') {
        this.closeAllFlyouts();
      }
    });
  }

  onMount() {
    this.siteOverlay = Core.Components.getComponent('site-overlay');
  }

  /**
   * Creates a new drawer modal which can be opened and closed.
   * @param {HTMLNode} content The content to be included within the flyout
   * @param {String} direction The direction the flyout should open from, if "left" will open from
   * the left instead of "right".
   * @returns {Flyout} The new flyout object
   */
  createFlyout(content) {
    const flyoutFragment = this.template.content.cloneNode(true);
    const flyoutEl = flyoutFragment.firstElementChild;

    const awaitingFlyoutEl = Core.Components.awaitComponents(flyoutEl);

    this.el.appendChild(flyoutEl);

    return awaitingFlyoutEl
      .then(components => components.find(component => component.el === flyoutEl))
      .then(flyout => {
        if (flyout && content) {
          flyout.populateContent(content);
        }

        return flyout;
      });
  }

  destroyFlyout(flyout) {
    if (!flyout) return;

    flyout.el.remove();
  }

  /**
   * Clears the content in the flyout
   * @param {*} flyout  The flyout to clear
   */
  clearFlyout(flyout) {
    if (!flyout) return;

    flyout.clear();
  }

  /**
   * Opens the flyout.
   * Will show the site overlay if available.
   * @param {*} flyout The flyout to be opened
   * @param {*} trigger The trigger that was clicked to open this flyout
   * @param {*} loading If true, will clear the content of the flyout and set the state to loading
   */
  openFlyout(flyout, trigger) {
    if (!flyout) return;

    if (this.siteOverlay) {
      this.siteOverlay.show();
    }

    flyout.open(trigger);
  }

  /**
   * Closes the flyout that is passed in.
   * @param {Flyout} flyout The flyout to be closed
   */
  closeFlyout(flyout) {
    if (!flyout) return;

    const { trigger } = flyout.state;

    flyout.close();

    const openFlyouts = this.children.filter(f => f.state.open);
    const nextFlyout = openFlyouts.length ? openFlyouts[openFlyouts.length - 1] : null;

    if (nextFlyout) {
      nextFlyout.focus();
    } else {
      if (this.siteOverlay) {
        this.siteOverlay.hide();
      }

      if (trigger) {
        trigger.focus();
      }
    }
  }

  /**
   * Closes all flyouts, returns focus to the first trigger, and removes the scroll lock.
   */
  closeAllFlyouts() {
    const flyouts = this.children;

    flyouts.forEach(flyout => this.closeFlyout(flyout));
  }
}
